import { z } from "zod";

import {
  canonicalJson,
  compareCodeUnits,
  sha256Hex,
  type JsonValue,
} from "./canonical.js";
import {
  CalcDecimal,
  decimalString,
  moneyString,
  percentString,
} from "./decimal.js";
import { calculateDm2016Service } from "./dm2016-calculation.js";
import {
  Dm2016FootnoteInputError,
  resolveDm2016FootnoteEffects,
} from "./dm2016-footnotes.js";
import {
  DM2016_POST_COMPENSATION_ROUNDING_POLICY_ID,
  DM2016_SERVICE_ROUNDING_POLICY_ID,
  DM2016_SUMMARY_ROUNDING_POLICY_IDS,
  MONEY_ROUNDING_MODE,
} from "./rounding-policy.js";

export const PUBLIC_TECHNICAL_ENGINE_VERSION = "0.3.0";
export const PUBLIC_TECHNICAL_FORMULA_ID = "dm2016-annex-i13-cp-v1-draft";

const identifierSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/);

const moneyInputSchema = z
  .string()
  .regex(
    /^(0|[1-9]\d{0,21})(\.\d{1,2})?$/,
    "Amount must be a plain decimal string with at most 2 decimal places",
  );

const positiveMoneyInputSchema = moneyInputSchema.refine(
  (value) => !/^0(?:\.0+)?$/.test(value),
  "Amount must be greater than zero",
);

const positiveCoefficientSchema = z
  .string()
  .regex(
    /^(0|[1-9]\d{0,7})(\.\d{1,12})?$/,
    "Coefficient must be a plain decimal string with at most 12 decimal places",
  )
  .refine(
    (value) => !/^0(?:\.0+)?$/.test(value),
    "Coefficient must be greater than zero",
  );

const expensePercentageSchema = z
  .string()
  .regex(
    /^(0|[1-9]\d?)(\.\d{1,6})?|100(?:\.0{1,6})?$/,
    "Expense percentage must be between 0 and 100 with at most 6 decimal places",
  );

export const publicTechnicalProcurementModelSchema = z.enum([
  "non_integrated",
  "integrated",
]);

const qCodeSchema = z.string().refine((code) => {
  const match = /^Qb(I|II|III)\.(\d{2})$/.exec(code);
  if (match === null) return false;
  const index = Number.parseInt(match[2] ?? "", 10);
  if (match[1] === "I") return index >= 1 && index <= 21;
  if (match[1] === "II") return index >= 1 && index <= 27;
  return index >= 1 && index <= 11;
}, "Q code is not included in Annex I.13 Table A");

const publicTechnicalQItemSchema = z
  .object({
    code: qCodeSchema,
    value: positiveCoefficientSchema,
  })
  .strict();

const publicTechnicalComponentSchema = z
  .object({
    componentId: identifierSchema,
    label: z.string().trim().min(1).max(200),
    valueV: positiveMoneyInputSchema,
    complexityG: positiveCoefficientSchema,
    qItems: z.array(publicTechnicalQItemSchema).min(1).max(100),
  })
  .strict()
  .superRefine((component, context) => {
    const codes = new Set<string>();
    component.qItems.forEach((item, index) => {
      if (codes.has(item.code)) {
        context.addIssue({
          code: "custom",
          message: `Duplicate Q code: ${item.code}`,
          path: ["qItems", index, "code"],
        });
      }
      codes.add(item.code);
    });
  });

const publicTechnicalAdditionalItemSchema = z
  .object({
    itemId: identifierSchema,
    label: z.string().trim().min(1).max(200),
    amount: positiveMoneyInputSchema,
    basis: z.enum(["analogy", "hourly"]),
  })
  .strict();

export const publicTechnicalDraftInputSchema = z
  .object({
    purpose: z.literal("verification_only"),
    rulesetVersion: identifierSchema,
    procurementModel: publicTechnicalProcurementModelSchema,
    tenderBaseProject: z.boolean().optional(),
    publicWorksConcession: z.boolean().optional(),
    bimMandatory: z.boolean(),
    expensePercentage: expensePercentageSchema,
    components: z.array(publicTechnicalComponentSchema).min(1).max(100),
    additionalItems: z.array(publicTechnicalAdditionalItemSchema).max(100),
  })
  .strict()
  .superRefine((input, context) => {
    const componentIds = new Set<string>();
    input.components.forEach((component, index) => {
      if (componentIds.has(component.componentId)) {
        context.addIssue({
          code: "custom",
          message: `Duplicate component ID: ${component.componentId}`,
          path: ["components", index, "componentId"],
        });
      }
      componentIds.add(component.componentId);

      try {
        resolveDm2016FootnoteEffects({
          services: component.qItems.map((item) => ({
            serviceCode: item.code,
            valueV: component.valueV,
          })),
          context: {
            procurementModel: input.procurementModel,
            ...(input.tenderBaseProject === undefined
              ? {}
              : { tenderBaseProject: input.tenderBaseProject }),
            ...(input.publicWorksConcession === undefined
              ? {}
              : { publicWorksConcession: input.publicWorksConcession }),
          },
        });
      } catch (error) {
        if (!(error instanceof Dm2016FootnoteInputError)) throw error;
        error.issues.forEach((issue) => {
          context.addIssue({
            code: "custom",
            message: issue.message,
            path: ["components", index, "qItems"],
          });
        });
      }
    });

    const additionalIds = new Set<string>();
    input.additionalItems.forEach((item, index) => {
      if (additionalIds.has(item.itemId)) {
        context.addIssue({
          code: "custom",
          message: `Duplicate additional item ID: ${item.itemId}`,
          path: ["additionalItems", index, "itemId"],
        });
      }
      additionalIds.add(item.itemId);
    });

    const totalWorksValue = input.components.reduce(
      (total, component) => total.plus(component.valueV),
      new CalcDecimal(0),
    );
    const maximum = expenseMaximumPercentage(totalWorksValue);
    if (new CalcDecimal(input.expensePercentage).gt(maximum)) {
      context.addIssue({
        code: "custom",
        message: `Expense percentage exceeds the statutory maximum ${decimalString(maximum.toDecimalPlaces(6))}%`,
        path: ["expensePercentage"],
      });
    }
  });

export type PublicTechnicalDraftInput = z.infer<
  typeof publicTechnicalDraftInputSchema
>;
export type PublicTechnicalProcurementModel = z.infer<
  typeof publicTechnicalProcurementModelSchema
>;
export type PublicTechnicalPhase = "pfte" | "executive";

export interface PublicTechnicalPhaseAllocation {
  readonly phase: PublicTechnicalPhase;
  readonly weight: "0.5" | "1";
}

export interface PublicTechnicalLineResult {
  readonly componentId: string;
  readonly componentLabel: string;
  readonly code: string;
  readonly valueV: string;
  readonly valueUsedForParameterP: string;
  readonly complexityG: string;
  readonly qValue: string;
  readonly parameterP: string;
  readonly parameterPPercentDisplay: string;
  readonly phaseAllocations: readonly PublicTechnicalPhaseAllocation[];
  readonly compensationRaw: string;
  readonly compensationRounded: string;
}

export interface PublicTechnicalAdditionalLineResult {
  readonly itemId: string;
  readonly label: string;
  readonly basis: "analogy" | "hourly";
  readonly amountRounded: string;
}

export interface PublicTechnicalDraftResult {
  readonly engineVersion: string;
  readonly formulaId: string;
  readonly rulesetVersion: string;
  readonly moneyRoundingMode: typeof MONEY_ROUNDING_MODE;
  readonly serviceRoundingPolicyId: typeof DM2016_SERVICE_ROUNDING_POLICY_ID;
  readonly compensationRoundingPolicyId:
    typeof DM2016_SUMMARY_ROUNDING_POLICY_IDS.sum_rounded_lines;
  readonly postCompensationRoundingPolicyId:
    typeof DM2016_POST_COMPENSATION_ROUNDING_POLICY_ID;
  readonly professionalUseAllowed: false;
  readonly checkedAsOf: "2026-08-15";
  readonly releaseBlockers: readonly string[];
  readonly normalizedInput: PublicTechnicalDraftInput;
  readonly lines: readonly PublicTechnicalLineResult[];
  readonly additionalLines: readonly PublicTechnicalAdditionalLineResult[];
  readonly totals: {
    readonly worksValue: string;
    readonly compensationRaw: string;
    readonly compensationRounded: string;
    readonly roundingDelta: string;
    readonly bimPercentage: "0" | "10";
    readonly bimIncrement: string;
    readonly compensationAfterBim: string;
    readonly expensePercentage: string;
    readonly expenseMaximumPercentage: string;
    readonly expenses: string;
    readonly totalBeforeTaxesAndContributions: string;
  };
}

export interface HashedPublicTechnicalDraftResult
  extends PublicTechnicalDraftResult {
  readonly inputSha256: string;
  readonly resultSha256: string;
}

export class PublicTechnicalInputError extends Error {
  readonly issues: z.core.$ZodIssue[];

  constructor(issues: z.core.$ZodIssue[]) {
    super("Public technical calculation input is invalid");
    this.name = "PublicTechnicalInputError";
    this.issues = issues;
  }
}

const LOWER_EXPENSE_VALUE = new CalcDecimal("1000000");
const UPPER_EXPENSE_VALUE = new CalcDecimal("25000000");
const MAX_EXPENSE_PERCENT = new CalcDecimal("25");
const MIN_EXPENSE_PERCENT = new CalcDecimal("10");
const BIM_PERCENT = new CalcDecimal("10");
type CalcDecimalValue = InstanceType<typeof CalcDecimal>;

const INTEGRATED_SPLIT_CODES = new Set([
  "QbIII.03",
  "QbIII.04",
  "QbIII.05",
  "QbIII.07",
]);

function deepFreeze<T>(value: T): Readonly<T> {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value).forEach((item) => deepFreeze(item));
  }
  return value;
}

function expenseMaximumPercentage(
  worksValue: CalcDecimalValue,
): CalcDecimalValue {
  if (worksValue.lte(LOWER_EXPENSE_VALUE)) return MAX_EXPENSE_PERCENT;
  if (worksValue.gte(UPPER_EXPENSE_VALUE)) return MIN_EXPENSE_PERCENT;

  const interval = UPPER_EXPENSE_VALUE.minus(LOWER_EXPENSE_VALUE);
  const reduction = worksValue
    .minus(LOWER_EXPENSE_VALUE)
    .times(MAX_EXPENSE_PERCENT.minus(MIN_EXPENSE_PERCENT))
    .div(interval);
  return MAX_EXPENSE_PERCENT.minus(reduction);
}

export function publicTechnicalExpenseMaximumPercentage(
  worksValue: string,
): string {
  const parsed = positiveMoneyInputSchema.safeParse(worksValue);
  if (!parsed.success) throw new PublicTechnicalInputError(parsed.error.issues);
  return decimalString(
    expenseMaximumPercentage(new CalcDecimal(parsed.data)).toDecimalPlaces(6),
  );
}

export function publicTechnicalPhaseAllocations(
  code: string,
  procurementModel: PublicTechnicalProcurementModel,
): readonly PublicTechnicalPhaseAllocation[] {
  const parsedCode = qCodeSchema.safeParse(code);
  const parsedModel = publicTechnicalProcurementModelSchema.safeParse(procurementModel);
  if (!parsedCode.success) throw new PublicTechnicalInputError(parsedCode.error.issues);
  if (!parsedModel.success) throw new PublicTechnicalInputError(parsedModel.error.issues);

  if (code === "QbII.08" && procurementModel === "integrated") {
    throw new PublicTechnicalInputError([
      {
        code: "custom",
        message: "QbII.08 is not applicable to integrated procurement under Annex I.13 article 2",
        path: [],
        input: code,
      },
    ]);
  }
  if (code.startsWith("QbI.")) return [{ phase: "pfte", weight: "1" }];
  if (code === "QbII.05") {
    return [{
      phase: procurementModel === "integrated" ? "pfte" : "executive",
      weight: "1",
    }];
  }
  if (code.startsWith("QbII.")) return [{ phase: "pfte", weight: "1" }];
  if (procurementModel === "integrated" && INTEGRATED_SPLIT_CODES.has(code)) {
    return [
      { phase: "pfte", weight: "0.5" },
      { phase: "executive", weight: "0.5" },
    ];
  }
  return [{ phase: "executive", weight: "1" }];
}

function normalizeInput(input: PublicTechnicalDraftInput): PublicTechnicalDraftInput {
  return {
    ...input,
    expensePercentage: decimalString(new CalcDecimal(input.expensePercentage)),
    components: input.components.map((component) => ({
      ...component,
      valueV: decimalString(new CalcDecimal(component.valueV)),
      complexityG: decimalString(new CalcDecimal(component.complexityG)),
      qItems: component.qItems
        .map((item) => ({
          code: item.code,
          value: decimalString(new CalcDecimal(item.value)),
        }))
        .sort((left, right) => compareCodeUnits(left.code, right.code)),
    })),
    additionalItems: input.additionalItems
      .map((item) => ({
        ...item,
        amount: moneyString(new CalcDecimal(item.amount)),
      }))
      .sort((left, right) => compareCodeUnits(left.itemId, right.itemId)),
  };
}

export function calculatePublicTechnicalDraft(
  rawInput: unknown,
): PublicTechnicalDraftResult {
  const parsed = publicTechnicalDraftInputSchema.safeParse(rawInput);
  if (!parsed.success) throw new PublicTechnicalInputError(parsed.error.issues);

  const normalizedInput = normalizeInput(parsed.data);
  const lines = normalizedInput.components.flatMap((component) => {
    return component.qItems.map((item): PublicTechnicalLineResult => {
      const calculation = calculateDm2016Service({
        serviceCode: item.code,
        valueV: component.valueV,
        complexityG: component.complexityG,
        qDefinition: { kind: "single", q: item.value },
        selector: { kind: "work_value" },
        parameterPMode: "total_work_value",
      });
      const band = calculation.bands[0];
      if (band === undefined) {
        throw new Error(`The shared D.M. 2016 engine returned no trace for ${item.code}`);
      }
      return {
        componentId: component.componentId,
        componentLabel: component.label,
        code: item.code,
        valueV: component.valueV,
        valueUsedForParameterP: band.valueUsedForParameterP,
        complexityG: component.complexityG,
        qValue: item.value,
        parameterP: band.parameterP,
        parameterPPercentDisplay: percentString(new CalcDecimal(band.parameterP)),
        phaseAllocations: publicTechnicalPhaseAllocations(
          item.code,
          normalizedInput.procurementModel,
        ),
        compensationRaw: calculation.compensationRaw,
        compensationRounded: calculation.compensationRounded,
      };
    });
  });

  const additionalLines = normalizedInput.additionalItems.map(
    (item): PublicTechnicalAdditionalLineResult => ({
      itemId: item.itemId,
      label: item.label,
      basis: item.basis,
      amountRounded: moneyString(new CalcDecimal(item.amount)),
    }),
  );
  const worksValue = normalizedInput.components.reduce(
    (total, component) => total.plus(component.valueV),
    new CalcDecimal(0),
  );
  const compensationRaw = lines.reduce(
    (total, line) => total.plus(line.compensationRaw),
    additionalLines.reduce(
      (total, line) => total.plus(line.amountRounded),
      new CalcDecimal(0),
    ),
  );
  const compensationRounded = lines.reduce(
    (total, line) => total.plus(line.compensationRounded),
    additionalLines.reduce(
      (total, line) => total.plus(line.amountRounded),
      new CalcDecimal(0),
    ),
  );
  const bimIncrement = normalizedInput.bimMandatory
    ? new CalcDecimal(moneyString(compensationRounded.times(BIM_PERCENT).div(100)))
    : new CalcDecimal(0);
  const compensationAfterBim = compensationRounded.plus(bimIncrement);
  const expenses = new CalcDecimal(
    moneyString(
      compensationAfterBim
        .times(normalizedInput.expensePercentage)
        .div(100),
    ),
  );
  const expenseMaximum = expenseMaximumPercentage(worksValue);

  return deepFreeze({
    engineVersion: PUBLIC_TECHNICAL_ENGINE_VERSION,
    formulaId: PUBLIC_TECHNICAL_FORMULA_ID,
    rulesetVersion: normalizedInput.rulesetVersion,
    moneyRoundingMode: MONEY_ROUNDING_MODE,
    serviceRoundingPolicyId: DM2016_SERVICE_ROUNDING_POLICY_ID,
    compensationRoundingPolicyId:
      DM2016_SUMMARY_ROUNDING_POLICY_IDS.sum_rounded_lines,
    postCompensationRoundingPolicyId:
      DM2016_POST_COMPENSATION_ROUNDING_POLICY_ID,
    professionalUseAllowed: false,
    checkedAsOf: "2026-08-15",
    releaseBlockers: [
      "I valori G e Q sono confermati; restano il riesame testuale e delle corrispondenze Z-1, l’integrazione completa degli input condizionali e l’approvazione professionale del payload.",
      "Questo motore resta un percorso di verifica e non deve essere usato per incarichi reali finché il release gate non è completamente aperto.",
    ],
    normalizedInput,
    lines,
    additionalLines,
    totals: {
      worksValue: decimalString(worksValue),
      compensationRaw: decimalString(compensationRaw),
      compensationRounded: moneyString(compensationRounded),
      roundingDelta: moneyString(
        compensationRounded.minus(new CalcDecimal(moneyString(compensationRaw))),
      ),
      bimPercentage: normalizedInput.bimMandatory ? "10" : "0",
      bimIncrement: moneyString(bimIncrement),
      compensationAfterBim: moneyString(compensationAfterBim),
      expensePercentage: normalizedInput.expensePercentage,
      expenseMaximumPercentage: decimalString(expenseMaximum.toDecimalPlaces(6)),
      expenses: moneyString(expenses),
      totalBeforeTaxesAndContributions: moneyString(
        compensationAfterBim.plus(expenses),
      ),
    },
  });
}

export async function calculatePublicTechnicalDraftWithHashes(
  input: unknown,
): Promise<HashedPublicTechnicalDraftResult> {
  const result = calculatePublicTechnicalDraft(input);
  const inputSha256 = await sha256Hex(
    canonicalJson(result.normalizedInput as unknown as JsonValue),
  );
  const resultSha256 = await sha256Hex(
    canonicalJson(result as unknown as JsonValue),
  );
  return deepFreeze({ ...result, inputSha256, resultSha256 });
}
